// AssetIcon — PlayCanvas-style thumbnails per asset kind. One component
// instead of N because the surrounding card/inspector wants identical
// sizing and a single fallback path when something doesn't load.
//
// Kind handling:
//   texture    → render the image at asset.url over a checkerboard.
//   audio      → AudioWaveform (separate component; see below).
//   body_anim  → cyan isometric cube (SVG). 3D scrubbing is a v2.
//   face_anim  → orange face glyph (SVG). Peak-frame morph render is a v2.
//   prefab     → purple stacked-cube glyph (SVG). Bundle of body+face+audio.
//   rig        → yellow figure glyph (SVG).
//   *          → gray crystal glyph.
//
// `size` controls both the outer box and the SVG inner dimensions so the
// same component works for a 22-px card badge or a 200-px inspector hero.

import React, { useEffect, useRef, useState } from 'react'
import AudioWaveform from './AudioWaveform'
import { captureAvatarSnapshot, getAvatarSnapshot, subscribeAvatarSnapshot } from '../../lib/avatarSnapshot'
import { materialThumbnailSrc } from '../../lib/materialThumbnail'
import { assetKindColor, blueprintGraphGlyph } from '../../lib/assetKindColors'

// Asset-kind identity colors live in one place (assetKindColors.js) — this
// map used to be duplicated here, in AssetCard and in Inspector.

export default function AssetIcon({ asset, size = 80, hoverable = true }) {
  const c = assetKindColor(asset?.kind)

  if (asset?.kind === 'audio' && asset.url) {
    return (
      <Frame size={size} color={c}>
        <AudioWaveform url={asset.url} width={size - 8} height={Math.max(20, size / 2)} color={c} />
      </Frame>
    )
  }

  if (asset?.kind === 'texture' && asset.url) {
    return (
      <Frame size={size} color={c} checker>
        <img
          src={asset.url}
          alt=""
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          onError={e => { e.currentTarget.style.display = 'none' }}
        />
      </Frame>
    )
  }

  if (asset?.kind === 'material') {
    // Prefer the real-render snapshot captured at graph-commit time; it's
    // faithful to the compiled material (fresnel/noise/lerp), unlike the CSS
    // MaterialSphere fallback which only approximates a base color/texture.
    const thumb = materialThumbnailSrc(asset)
    const type = (asset?.materialType ?? 'pbr').toUpperCase()
    return (
      <Frame size={size} color={c}>
        {thumb ? (
          <>
            <img
              src={thumb}
              alt=""
              draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            {size >= 60 && <TypeBadge type={type} color={c} />}
          </>
        ) : (
          <MaterialSphere asset={asset} color={c} size={size} />
        )}
      </Frame>
    )
  }

  if (asset?.kind === 'body_anim' || asset?.kind === 'face_anim') {
    return <AvatarSnapshotThumb size={size} color={c} kind={asset.kind} hoverable={hoverable} />
  }

  return (
    <Frame size={size} color={c}>
      <Glyph kind={asset?.kind ?? 'fallback'} size={size} color={c}
        glyphChar={blueprintGraphGlyph(asset?.graphKind)} />
    </Frame>
  )
}

// ─── Avatar-snapshot thumbnail ──────────────────────────────────────────────
// Body/face anims share the same idle pose so we reuse a single snapshot of
// the live avatar canvas as the baseline image — same approach as
// ClipsPanel. On hover the snapshot is swapped for a captureStream so the
// thumbnail "comes alive" if the avatar is currently animating.

function AvatarSnapshotThumb({ size, color, kind, hoverable }) {
  const [snapshot, setSnapshot] = useState(getAvatarSnapshot())
  const [hover, setHover] = useState(false)
  const videoRef = useRef(null)

  useEffect(() => {
    // Capture a fresh snapshot when we mount the first time, so the first
    // card render gets a real image instead of the fallback glyph.
    if (!snapshot) {
      // Defer one frame so the avatar has rendered at least once.
      const id = requestAnimationFrame(() => {
        const url = captureAvatarSnapshot()
        if (url) setSnapshot(url)
      })
      return () => cancelAnimationFrame(id)
    }
  }, [snapshot])

  useEffect(() => subscribeAvatarSnapshot(() => setSnapshot(getAvatarSnapshot())), [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (hover && hoverable) {
      const canvas = window.__AVATAR_RENDERER__?.domElement
      if (!canvas) return
      try {
        video.srcObject = canvas.captureStream(30)
        video.play().catch(() => {})
      } catch { /* ignore */ }
    } else {
      video.srcObject = null
    }
  }, [hover, hoverable])

  return (
    <Frame
      size={size}
      color={color}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {snapshot ? (
        <img
          src={snapshot}
          alt=""
          draggable={false}
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            opacity: hover && hoverable ? 0 : 1,
            transition: 'opacity 0.15s',
            pointerEvents: 'none',
          }}
        />
      ) : (
        <Glyph kind={kind} size={size} color={color} />
      )}
      <video
        ref={videoRef}
        muted
        playsInline
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover',
          opacity: hover && hoverable ? 1 : 0,
          transition: 'opacity 0.15s',
          pointerEvents: 'none',
        }}
      />
      {/* Kind tint badge in the corner so body vs face is visually distinct */}
      <div style={{
        position: 'absolute', top: 4, right: 4,
        fontFamily: 'var(--m-font)', fontSize: 8, fontWeight: 700,
        letterSpacing: '.1em',
        color: color, background: '#0c0e15cc',
        padding: '1px 4px', borderRadius: 2,
        border: `1px solid ${color}55`,
      }}>{kind === 'body_anim' ? 'BODY' : 'FACE'}</div>
    </Frame>
  )
}

// ─── Frame ──────────────────────────────────────────────────────────────────
// A square card with a subtle tint matching the kind color, plus an optional
// checkerboard background for transparent textures.

function Frame({ size, color, checker, children, onMouseEnter, onMouseLeave }) {
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        width: size, height: size,
        borderRadius: 4,
        background: checker
          ? `repeating-conic-gradient(#1d1f28 0 25%, #14161e 0 50%) 50% / 16px 16px`
          : `linear-gradient(140deg, ${color}10, #14161e)`,
        border: `1px solid ${color}33`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {children}
    </div>
  )
}

// ─── Material sphere swatch ─────────────────────────────────────────────────
// Glossy 3D sphere preview that mirrors the bound diffuse map (if any) plus
// a sphere-shading overlay (CSS radial gradient acting as a fake highlight
// + terminator). PlayCanvas-style PBR thumbnail without spinning up a
// per-card WebGL context.

function MaterialSphere({ asset, color, size }) {
  // PBR materials expose `diffuse`/`diffuseMap` directly. A GRAPH material has
  // neither — its look lives in `data.graph` — so without this it fell back to
  // the flat purple accent tint. Derive a representative base from the graph:
  // prefer its Texture Sample's texture, else a Constant/BSDF color.
  let diffuse = asset?.data?.diffuse
  let diffuseMapId = asset?.data?.diffuseMap
  if ((asset?.materialType ?? '') === 'graph') {
    const nodes = asset?.data?.graph?.nodes ?? []
    const texNode = nodes.find(n => n.type === 'TextureSample' && n.data?.textureId)
    if (texNode) {
      diffuseMapId = texNode.data.textureId
    } else {
      const colorNode =
        nodes.find(n => n.type === 'ConstantColor' && Array.isArray(n.data?.value)) ??
        nodes.find(n => n.type === 'PrincipledBSDF' && Array.isArray(n.data?.color))
      diffuse = colorNode?.data?.value ?? colorNode?.data?.color ?? diffuse
    }
  }

  const tint = Array.isArray(diffuse) && diffuse.length === 3
    ? `rgb(${Math.round(diffuse[0] * 255)},${Math.round(diffuse[1] * 255)},${Math.round(diffuse[2] * 255)})`
    : color

  // Resolve the diffuse texture URL through CustomAssetManager — the
  // material row stores a texture_asset UUID, not a URL. Best-effort: if
  // the texture isn't in the cache yet, we just fall back to the tint
  // gradient (no flash of missing image).
  let diffuseUrl = null
  if (diffuseMapId && typeof window !== 'undefined' && window.__ASSET_REGISTRY__) {
    for (const r of window.__ASSET_REGISTRY__.list()) {
      if (r.kind === 'texture' && r.rawId === diffuseMapId) {
        diffuseUrl = (r.url ?? '').split('#')[0]
        break
      }
    }
  }

  const type = (asset?.materialType ?? 'pbr').toUpperCase()
  const inner = Math.round(size * 0.7)
  // Compose the sphere: texture (or tint) as base, multiply-shaded by a
  // radial gradient that goes white→transparent→black to fake highlight,
  // mid-tone, and terminator zones.
  const baseLayer = diffuseUrl
    ? `url(${diffuseUrl}) center / cover`
    : `${tint}`
  const shading = `radial-gradient(circle at 32% 28%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0) 22%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.7) 100%)`
  return (
    <>
      <div style={{
        width: inner, height: inner,
        borderRadius: '50%',
        background: baseLayer,
        boxShadow: `0 0 ${Math.round(size * 0.08)}px ${color}33`,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          borderRadius: '50%',
          background: shading,
          mixBlendMode: 'overlay',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          borderRadius: '50%',
          boxShadow: `inset -${Math.round(size * 0.1)}px -${Math.round(size * 0.1)}px ${Math.round(size * 0.18)}px rgba(0,0,0,0.55)`,
        }} />
      </div>
      {size >= 60 && <TypeBadge type={type} color={color} />}
    </>
  )
}

// Corner badge showing the material type (PBR / TOON / SKIN / GRAPH). Shared by
// the CSS-sphere fallback and the real-render thumbnail so both look identical.
function TypeBadge({ type, color }) {
  return (
    <div style={{
      position: 'absolute', bottom: 4, right: 4,
      fontFamily: 'var(--m-font)', fontSize: 8, fontWeight: 700,
      letterSpacing: '.1em',
      color: color, background: '#0c0e15cc',
      padding: '1px 4px', borderRadius: 2,
      border: `1px solid ${color}55`,
    }}>{type}</div>
  )
}

// ─── Glyphs ─────────────────────────────────────────────────────────────────

function Glyph({ kind, size, color, glyphChar }) {
  const inner = Math.round(size * 0.55)
  const props = { width: inner, height: inner, viewBox: '0 0 100 100', fill: 'none' }

  if (kind === 'body_anim') {
    // Isometric cube — same vibe as PlayCanvas's mesh asset icon.
    return (
      <svg {...props}>
        <path d="M50 14 L86 32 L86 68 L50 86 L14 68 L14 32 Z"
          stroke={color} strokeWidth="3" strokeLinejoin="round" />
        <path d="M50 14 L50 86 M14 32 L50 50 L86 32"
          stroke={color} strokeWidth="3" strokeLinejoin="round" opacity="0.7" />
      </svg>
    )
  }
  if (kind === 'blueprint_asset' && glyphChar) {
    // The outliner's own glyph for this graph (⬡ body / ◎ face / ↯ events), so
    // one graph reads the same in the outliner, the tool rail and here.
    return (
      <span style={{
        fontSize: Math.round(size * 0.42), color, lineHeight: 1,
        fontFamily: 'var(--m-font)',
      }}>{glyphChar}</span>
    )
  }
  if (kind === 'blueprint_asset') {
    // Three wired nodes — the blueprint editor's own shape (a graph), not a
    // document icon: what is stored here is the node graph, not a file.
    return (
      <svg {...props}>
        <rect x="10" y="18" width="26" height="18" rx="3" stroke={color} strokeWidth="3" />
        <rect x="62" y="10" width="28" height="16" rx="3" stroke={color} strokeWidth="3" opacity="0.75" />
        <rect x="62" y="52" width="28" height="16" rx="3" stroke={color} strokeWidth="3" opacity="0.75" />
        <rect x="10" y="62" width="26" height="18" rx="3" stroke={color} strokeWidth="3" opacity="0.55" />
        <path d="M36 27 Q50 27 50 18 L62 18" stroke={color} strokeWidth="3" strokeLinecap="round" />
        <path d="M36 27 Q50 27 50 60 L62 60" stroke={color} strokeWidth="3" strokeLinecap="round" opacity="0.8" />
        <circle cx="50" cy="27" r="3.5" fill={color} />
      </svg>
    )
  }
  if (kind === 'face_anim') {
    // Face mask outline with two eye dots.
    return (
      <svg {...props}>
        <path d="M50 10 C72 10 85 28 85 50 C85 75 70 90 50 90 C30 90 15 75 15 50 C15 28 28 10 50 10 Z"
          stroke={color} strokeWidth="3" />
        <circle cx="38" cy="44" r="4" fill={color} />
        <circle cx="62" cy="44" r="4" fill={color} />
        <path d="M36 64 Q50 74 64 64" stroke={color} strokeWidth="3" strokeLinecap="round" />
      </svg>
    )
  }
  if (kind === 'prefab') {
    // Stacked cubes — a bundle.
    return (
      <svg {...props}>
        <path d="M50 8 L78 22 L78 50 L50 64 L22 50 L22 22 Z"
          stroke={color} strokeWidth="3" strokeLinejoin="round" opacity="0.7" />
        <path d="M50 36 L78 50 L78 78 L50 92 L22 78 L22 50 Z"
          stroke={color} strokeWidth="3" strokeLinejoin="round" fill="#14161e" />
        <path d="M22 50 L50 64 L78 50 M50 64 L50 92"
          stroke={color} strokeWidth="3" strokeLinejoin="round" />
      </svg>
    )
  }
  if (kind === 'rig') {
    // Stick figure / armature.
    return (
      <svg {...props}>
        <circle cx="50" cy="22" r="10" stroke={color} strokeWidth="3" />
        <path d="M50 32 L50 60 M30 44 L70 44 M50 60 L34 88 M50 60 L66 88"
          stroke={color} strokeWidth="3" strokeLinecap="round" />
      </svg>
    )
  }
  if (kind === 'audio') {
    // Fallback (no URL) — speaker glyph.
    return (
      <svg {...props}>
        <path d="M18 38 L36 38 L56 22 L56 78 L36 62 L18 62 Z"
          stroke={color} strokeWidth="3" strokeLinejoin="round" fill="#14161e" />
        <path d="M66 36 Q74 50 66 64 M76 28 Q88 50 76 72"
          stroke={color} strokeWidth="3" strokeLinecap="round" />
      </svg>
    )
  }

  // Fallback crystal/diamond.
  return (
    <svg {...props}>
      <path d="M50 12 L82 40 L50 88 L18 40 Z M18 40 L82 40 M50 12 L50 88 M34 26 L66 26"
        stroke={color} strokeWidth="3" strokeLinejoin="round" />
    </svg>
  )
}
